import React, { ReactNode, useEffect, useState } from "react";
import clsx from "clsx";
import { CloseOutlined } from "@ant-design/icons";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  showCloseIcon?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  backdropClosable?: boolean;
  blurBackdrop?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  showCloseIcon = true,
  size = "md",
  backdropClosable = true,
  blurBackdrop = false,
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  const handleBackdropClick = () => {
    if (backdropClosable) onClose();
  };

  if (!isVisible) return null;

  return (
    <div
      className={clsx(
        "fixed inset-0 z-[60] flex items-center justify-center transition-opacity duration-300",
        blurBackdrop ? "backdrop-blur-sm bg-black/30" : "bg-black bg-opacity-50",
        isOpen ? "opacity-100" : "opacity-0"
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={clsx(
          "relative bg-white rounded-lg shadow-lg w-full mx-4 p-6 transform transition-all duration-300",
          isOpen ? "scale-100 opacity-100 animate-fadeInUp" : "scale-95 opacity-0",
          sizeClasses[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-start ${title ? "justify-between" : "justify-end"} mb-4`}>
          {title && (
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          )}
          {showCloseIcon && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition"
              aria-label="Close modal"
            >
              <CloseOutlined className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="text-gray-700">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="mt-6 flex justify-end items-center gap-2">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default Modal;
